/**
 * DeliveryCheck.jsx
 * What Meta says about one advert, level by level.
 *
 * "I cannot find my ad" has several causes that look identical from the
 * outside: waiting for review, rejected, or switched off a level up. Meta
 * knows which it is; its interface does not always say. So this asks, and
 * prints the answer plainly.
 *
 * It also prints the ids and a direct Ads Manager link. Ads Manager lists
 * every ad on the account however it was made, so it is the reliable place to
 * look — Business Suite's own ads screens are filtered in ways that are not
 * documented, and are not something to diagnose from.
 *
 * Dependencies: services/api.js, components/Icons.jsx
 */

import { useState } from 'react';
import { api } from '../../services/api.js';
import { Check, Refresh, Warning } from '../Icons.jsx';

/** Meta's status words, said the way a person would say them. */
const MEANING = {
  ACTIVE: 'Running and eligible to be shown.',
  PENDING_REVIEW: 'Waiting for Meta to review it. Nothing delivers until it passes.',
  IN_PROCESS: 'Meta is still setting it up.',
  DISAPPROVED: 'Rejected by Meta. The reason is below.',
  WITH_ISSUES: 'Running, but Meta has flagged something. See below.',
  PAUSED: 'Paused at this level.',
  CAMPAIGN_PAUSED: 'The campaign above it is paused, so it cannot run.',
  ADSET_PAUSED: 'The ad set above it is paused, so it cannot run.',
  DELETED: 'Deleted at Meta.',
  ARCHIVED: 'Archived at Meta.',
  PREAPPROVED: 'Provisionally approved; full review still to come.',
};

export default function DeliveryCheck({ boostId, onClose }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function check() {
    setBusy(true);
    setError(null);
    try {
      setData(await api.ads.delivery(boostId));
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  if (!data && !busy && !error) {
    return (
      <button className="btn btn--sm" onClick={check}>
        <Refresh /> Ask Meta
      </button>
    );
  }

  if (busy) return <span className="mono">Asking Meta…</span>;

  if (error) {
    return (
      <div className="notice notice--error">
        <Warning />
        <span>{error.message}</span>
      </div>
    );
  }

  if (data.problem) {
    return (
      <div className="notice notice--error">
        <Warning />
        <span>{data.problem}</span>
      </div>
    );
  }

  // Straight to this campaign rather than to the account's whole list, and
  // with a date range that includes today. Meta's "Last N days" presets end
  // yesterday, so a campaign started today is missing from every one of them —
  // which reads as the advert having vanished.
  const accountId = data.adAccount?.replace('act_', '');
  const campaignId = data.campaign?.id;
  const managerUrl = accountId
    ? `https://adsmanager.facebook.com/adsmanager/manage/campaigns?act=${accountId}` +
      (campaignId ? `&selected_campaign_ids=${campaignId}` : '') +
      '&date=maximum'
    : null;

  return (
    <div className="delivery">
      <Level label="Campaign" obj={data.campaign} />
      <Level label="Ad set" obj={data.adset} />
      {(data.ads ?? []).map((ad, i) => (
        <Level key={ad.adId ?? i} label={`Ad ${i + 1}`} obj={ad} />
      ))}

      <Figures insights={data.insights} />

      {managerUrl && (
        <p className="hint">
          Every advert on this ad account appears in Ads Manager, however it was made.{' '}
          <a href={managerUrl} target="_blank" rel="noopener noreferrer">
            Open this campaign in Ads Manager
          </a>
          . If it is not listed, check the date range includes today — Meta's &ldquo;Last 30
          days&rdquo; and similar presets end yesterday — and that you are signed in to Facebook as
          the profile that owns this ad account.
        </p>
      )}

      <button className="btn btn--sm" onClick={onClose}>
        Close
      </button>
    </div>
  );
}

/**
 * What it has actually done, from Meta's own figures.
 *
 * The point of showing these here is that they are the only proof that
 * matters: a status of "Active" is Meta's willingness to show the ad, while
 * an impression count is somebody having seen it. A new ad legitimately sits
 * at zero for a while, so nothing here is reported as a fault.
 */
function Figures({ insights }) {
  if (!insights) {
    return (
      <p className="hint">
        No figures from Meta yet. A new advert usually shows nothing for the first hour or two,
        and nothing at all until it has cleared review.
      </p>
    );
  }

  const spent = `£${((insights.spendPence ?? 0) / 100).toFixed(2)}`;
  const seen = insights.impressions ?? 0;

  return (
    <div className="delivery__figures">
      <Figure value={seen.toLocaleString()} label="Times shown" />
      <Figure value={(insights.reach ?? 0).toLocaleString()} label="People reached" />
      <Figure value={(insights.linkClicks ?? insights.clicks ?? 0).toLocaleString()} label="Clicks" />
      <Figure value={spent} label="Spent so far" />

      {seen === 0 && (
        <p className="hint" style={{ gridColumn: '1 / -1' }}>
          Nothing shown yet. If the statuses above all say active, it has been accepted and is
          waiting to be delivered — that is normal for the first hours of a new advert.
        </p>
      )}
    </div>
  );
}

const Figure = ({ value, label }) => (
  <div className="delivery__figure">
    <div className="delivery__figure-value">{value}</div>
    <div className="delivery__figure-label">{label}</div>
  </div>
);

function Level({ label, obj }) {
  if (!obj) return null;

  if (obj.error) {
    return (
      <div className="delivery__row">
        <span className="delivery__label">{label}</span>
        <span className="delivery__state delivery__state--bad">Meta could not answer</span>
        <span className="delivery__note">{obj.error}</span>
      </div>
    );
  }

  const state = obj.effectiveStatus ?? obj.status ?? 'unknown';
  const bad = ['DISAPPROVED', 'DELETED', 'ARCHIVED'].includes(state);
  const good = state === 'ACTIVE';

  return (
    <div className="delivery__row">
      <span className="delivery__label">{label}</span>
      <span
        className={`delivery__state${good ? ' delivery__state--ok' : bad ? ' delivery__state--bad' : ''}`}
      >
        {good && <Check size={13} />}
        {state.replaceAll('_', ' ').toLowerCase()}
      </span>
      <span className="delivery__note">
        {MEANING[state] ?? 'Meta did not explain this one.'}
        {(obj.issues ?? []).map((issue, i) => (
          <span key={i} className="delivery__issue">
            {issue.summary ?? issue.message ?? `Issue ${issue.code}`}
            {issue.message && issue.summary ? ` — ${issue.message}` : ''}
          </span>
        ))}
      </span>
      {obj.adId && <span className="delivery__id">{obj.adId}</span>}
      {obj.id && <span className="delivery__id">{obj.id}</span>}
    </div>
  );
}
